"use client";

import Link from "next/link";
import { ArrowLeft, ArrowRight, Calendar, Clock, Plus, User } from "lucide-react";
import { DemoButton } from "@/components/demo-button";
import { cn } from "@/lib/utils";
import { SkyPageHero } from "./sky-page-hero";
import { Reveal, SOFT, Sky } from "./sky-ui";

/* ────────────────────────────────────────────────────────────
   Klocki artykułów bloga w stylu „Sky".
   Strona: <SiteHeader overSky /> → <main className="overflow-x-clip">
   → <ArticleHero …/> → <ArticleBody>…</ArticleBody> → <Footer />.
   ──────────────────────────────────────────────────────────── */

/** Klasy komórek tabeli — kolumna Vetiny wyróżniona. Używaj w <TableCard>. */
export const TH = "p-4 text-left font-semibold text-foreground";
export const TH_VETINA = "p-4 text-left font-semibold bg-primary text-white";
export const TD = "p-4 align-top text-muted-foreground";
export const TD_LABEL = "p-4 align-top font-medium text-foreground";
export const TD_VETINA = "p-4 align-top bg-primary/[0.06] font-medium text-foreground";

/** Biała plakietka na niebie (data, czas czytania, autor). */
export function MetaChip({ icon: Icon, children }: { icon?: React.ElementType; children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1.5 text-sm font-medium text-white ring-1 ring-white/30 backdrop-blur">
      {Icon && <Icon className="h-3.5 w-3.5" />}
      {children}
    </span>
  );
}

/**
 * Hero artykułu: niebo + białe okruszki + H1 + lead + plakietki meta.
 * `category` trafia do eyebrow; `readTime`/`date`/`author` jako białe chipy;
 * `children` — dodatkowe chipy lub treść pod meta.
 */
export function ArticleHero({
  title,
  lead,
  breadcrumbs,
  category,
  readTime,
  date,
  author,
  children,
}: {
  title: React.ReactNode;
  lead?: React.ReactNode;
  breadcrumbs: { label: string; href: string }[];
  category?: string;
  readTime?: string;
  date?: string;
  author?: string;
  children?: React.ReactNode;
}) {
  const hasMeta = readTime || date || author || children;
  return (
    <SkyPageHero eyebrow={category} title={title} lead={lead} breadcrumbs={breadcrumbs} align="left" size="compact">
      {hasMeta && (
        <div className="flex flex-wrap items-center gap-2">
          {readTime && <MetaChip icon={Clock}>{readTime}</MetaChip>}
          {date && <MetaChip icon={Calendar}>{date}</MetaChip>}
          {author && <MetaChip icon={User}>{author}</MetaChip>}
          {children}
        </div>
      )}
    </SkyPageHero>
  );
}

/** Kolumna treści (~max-w-3xl) na białym tle, z wygodną interlinią. */
export function ArticleBody({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        "mx-auto w-full max-w-3xl px-4 pb-20 pt-12 text-[17px] leading-[1.75] text-foreground sm:px-6 md:pb-28 md:pt-16",
        className
      )}
    >
      {children}
    </div>
  );
}

/** Spis treści jako miękka karta. */
export function Toc({ items, title = "Spis treści" }: { items: { href: string; label: React.ReactNode }[]; title?: string }) {
  return (
    <nav className="rounded-[20px] p-6 ring-1 ring-black/[0.04] md:p-7" style={{ background: SOFT }}>
      <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-primary">{title}</h2>
      <ol className="space-y-2.5 text-[15px] leading-snug">
        {items.map((it) => (
          <li key={it.href}>
            <a href={it.href} className="font-medium text-foreground transition-colors hover:text-primary">
              {it.label}
            </a>
          </li>
        ))}
      </ol>
    </nav>
  );
}

/** Sekcja artykułu z H2 i kotwicą. `reveal` — animacja wejścia całego bloku. */
export function ArticleSection({
  id,
  title,
  children,
  reveal = true,
  className,
}: {
  id?: string;
  title?: React.ReactNode;
  children: React.ReactNode;
  reveal?: boolean;
  className?: string;
}) {
  const inner = (
    <section id={id} className={cn("scroll-mt-28 pt-14 md:pt-16", className)}>
      {title && <h2 className="mb-6 text-balance text-2xl font-bold tracking-tight md:text-3xl">{title}</h2>}
      {children}
    </section>
  );
  return reveal ? <Reveal>{inner}</Reveal> : inner;
}

/** Nagłówek H2 bez wrappera sekcji (gdy struktura strony jest inna). */
export function ArticleH2({ id, children, className }: { id?: string; children: React.ReactNode; className?: string }) {
  return (
    <h2 id={id} className={cn("mb-6 mt-14 scroll-mt-28 text-balance text-2xl font-bold tracking-tight md:mt-16 md:text-3xl", className)}>
      {children}
    </h2>
  );
}

/**
 * Miękka karta w treści. `tone`: "soft" (szary), "primary" (niebieska poświata),
 * "warning" (bursztynowa ikona). Ikona w kółku, gdy podana.
 */
export function Callout({
  icon: Icon,
  title,
  tone = "soft",
  children,
  className,
}: {
  icon?: React.ElementType;
  title?: React.ReactNode;
  tone?: "soft" | "primary" | "warning";
  children?: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "my-8 flex items-start gap-4 rounded-[20px] p-5 md:p-6",
        tone === "primary" ? "bg-primary/[0.05] ring-1 ring-primary/10" : "bg-[#F5F7FB] ring-1 ring-black/[0.04]",
        className
      )}
    >
      {Icon && (
        <span
          className={cn(
            "grid h-10 w-10 shrink-0 place-items-center rounded-full text-white",
            tone === "warning" ? "bg-amber-500" : "bg-primary"
          )}
        >
          <Icon className="h-5 w-5" strokeWidth={1.75} />
        </span>
      )}
      <div className="min-w-0 flex-1 text-[15px] leading-relaxed md:text-base">
        {title && <h3 className="mb-1.5 font-semibold text-foreground">{title}</h3>}
        {children}
      </div>
    </div>
  );
}

/** Biała karta z tabelą; na mobile przewija się poziomo wewnątrz karty. */
export function TableCard({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        "my-8 overflow-x-auto rounded-[22px] bg-white shadow-[0_24px_60px_-44px_rgba(5,40,242,0.45)] ring-1 ring-black/[0.06]",
        "[&_table]:w-full [&_table]:min-w-[520px] [&_table]:border-collapse [&_table]:text-sm [&_tbody_tr]:border-t [&_tbody_tr]:border-black/[0.05]",
        className
      )}
    >
      {children}
    </div>
  );
}

/** Numerowane kroki (kółko z numerem w kolorze marki). */
export function Steps({ items }: { items: { title: React.ReactNode; text?: React.ReactNode }[] }) {
  return (
    <ol className="my-8 space-y-5">
      {items.map((s, i) => (
        <li key={i} className="flex gap-4">
          <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-primary text-sm font-bold text-white">
            {i + 1}
          </span>
          <div className="min-w-0 pt-1">
            <h3 className="font-semibold leading-snug">{s.title}</h3>
            {s.text && <div className="mt-1 text-[15px] leading-relaxed text-muted-foreground">{s.text}</div>}
          </div>
        </li>
      ))}
    </ol>
  );
}

/** Rozwijane FAQ (wygląd jak na /booking). Treść JSON-LD zostaje w stronie. */
export function ArticleFaq({ items }: { items: { q: React.ReactNode; a: React.ReactNode }[] }) {
  return (
    <div className="my-8 space-y-2">
      {items.map((f, i) => (
        <details
          key={i}
          className="group rounded-2xl bg-[#F5F7FB] px-5 ring-1 ring-black/[0.04] open:bg-white open:shadow-[0_16px_40px_-28px_rgba(5,40,242,0.45)]"
        >
          <summary className="flex cursor-pointer list-none items-center justify-between gap-4 py-4 text-base font-medium text-foreground [&::-webkit-details-marker]:hidden">
            {f.q}
            <Plus className="h-4 w-4 shrink-0 text-primary transition group-open:rotate-45" />
          </summary>
          <div className="pb-5 text-[15px] leading-relaxed text-muted-foreground">{f.a}</div>
        </details>
      ))}
    </div>
  );
}

/**
 * Panel CTA na głębokim niebie z białym przyciskiem DemoButton.
 * `size="sm"` — w środku artykułu, domyślnie — duży na końcu.
 * `children` zastępuje domyślny przycisk (np. własne linki / CallbackForm).
 */
export function ArticleCta({
  title,
  text,
  source,
  buttonLabel = "Umów bezpłatne demo",
  note,
  size = "lg",
  children,
  className,
}: {
  title: React.ReactNode;
  text?: React.ReactNode;
  source?: string;
  buttonLabel?: React.ReactNode;
  note?: React.ReactNode;
  size?: "sm" | "lg";
  children?: React.ReactNode;
  className?: string;
}) {
  const lg = size === "lg";
  return (
    <Reveal className={cn(lg ? "mt-16" : "my-10", className)}>
      <div
        className={cn(
          "relative overflow-hidden text-center text-white",
          lg ? "rounded-[28px] px-6 py-12 md:rounded-[36px] md:px-12 md:py-16" : "rounded-[24px] px-6 py-8 md:px-10 md:py-10"
        )}
      >
        <Sky variant="deep" />
        <div className="relative">
          {lg ? (
            <h2 className="text-balance text-2xl font-bold tracking-tight md:text-4xl">{title}</h2>
          ) : (
            <p className="text-balance text-xl font-bold tracking-tight md:text-2xl">{title}</p>
          )}
          {text && <p className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-white/90 md:text-lg">{text}</p>}
          <div className="mt-7">
            {children ??
              (source && (
                <DemoButton
                  size="lg"
                  source={source}
                  className="h-12 bg-white px-7 text-base font-semibold text-primary shadow-xl shadow-[#021A9E]/30 hover:bg-white/90"
                >
                  {buttonLabel}
                  <ArrowRight className="ml-2 size-4" />
                </DemoButton>
              ))}
          </div>
          {note && <p className="mt-5 text-sm text-white/85">{note}</p>}
        </div>
      </div>
    </Reveal>
  );
}

/** Siatka powiązanych artykułów — białe karty ze strzałką. */
export function RelatedArticles({
  items,
  title = "Powiązane artykuły",
}: {
  items: { href: string; title: React.ReactNode; description?: React.ReactNode }[];
  title?: string;
}) {
  return (
    <Reveal className="mt-16">
      <h2 className="mb-6 text-xl font-bold tracking-tight md:text-2xl">{title}</h2>
      <div className="grid gap-4 md:grid-cols-2">
        {items.map((it) => (
          <Link
            key={it.href}
            href={it.href}
            className="group flex items-center gap-4 rounded-[20px] bg-white p-5 shadow-[0_16px_40px_-32px_rgba(2,26,158,0.4)] ring-1 ring-black/[0.06] transition hover:-translate-y-0.5 hover:ring-primary/30"
          >
            <div className="min-w-0 flex-1">
              <h3 className="font-semibold leading-snug transition-colors group-hover:text-primary">{it.title}</h3>
              {it.description && <p className="mt-1 text-sm text-muted-foreground">{it.description}</p>}
            </div>
            <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-primary/[0.08] text-primary transition group-hover:bg-primary group-hover:text-white">
              <ArrowRight className="h-4 w-4" />
            </span>
          </Link>
        ))}
      </div>
    </Reveal>
  );
}

/** Link powrotu do bloga na dole artykułu. */
export function BackToBlog({ label = "Wróć do bloga" }: { label?: string }) {
  return (
    <div className="mt-12 border-t border-black/[0.06] pt-8">
      <Link
        href="/blog"
        className="inline-flex items-center gap-2 text-[15px] font-medium text-muted-foreground transition-colors hover:text-primary"
      >
        <ArrowLeft className="size-4" />
        {label}
      </Link>
    </div>
  );
}
